import moment from "moment";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import styled from "styled-components";
import CaretIcon from "sweet-web/components/icons/CaretIcon";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import { images } from "sweet-assets";
import * as selectors from "sweet-web/redux/selectors";

const Wrapper = styled.div<{ active: boolean }>`
  width: 100%;
  padding: 10px 15px 10px 15px;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  background-color: ${props =>
    props.active ? colors.lighterGray : colors.white};
  &:hover {
    background-color: ${colors.lighterGray};
    cursor: default;
  }
  @media screen and (max-width: 600px) {
    /* mobile styling */
    padding: 15px 0;
    border-top: 1px solid ${colors.lighterGray};
    &:first-of-type {
      border: none;
    }
  }
`;

const LineCaret = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  padding: 10px;
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: 600px) {
    /* mobile styling */
    display: flex;
  }
`;

const LineImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 20px;
  margin-right: 15px;
`;

const LineInitials = styled.div<{ active: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 20px;
  margin-right: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.gray};
  background-color: ${colors.lighterGray};
  text-align: center;
`;

const LineText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const LineTitle = styled.h3`
  font-size: 17px;
  color: ${colors.nearBlack};
  font-weight: 600;
`;

const LineLabel = styled.p`
  font-size: 12px;
  color: ${colors.gray};
  font-weight: 500;
`;

type OwnProps = {
  chatId: string;
};

type Props = OwnProps & RouteComponentProps & ConnectedProps<typeof connector>;

const ChatLine: React.FC<Props> = props => {
  const params = props.match.params as { chatId: string };
  const activeChatId = params.chatId;
  const {
    chatId,
    userType,
    chat,
    otherChatUserName,
    otherChatUserType,
    otherChatUserImage,
    otherChatUserInitials
  } = props;

  const onClick = () => {
    if (activeChatId !== chatId) props.history.push(`/c/${chat.id}`);
  };

  return (
    <Wrapper
      key={`chat-line-${chat.id}`}
      active={chatId === activeChatId}
      onClick={onClick}
    >
      {otherChatUserImage && <LineImage src={otherChatUserImage} />}
      {!otherChatUserImage && (
        <LineInitials active={chatId === activeChatId}>
          {otherChatUserInitials}
        </LineInitials>
      )}
      <LineText>
        <LineTitle>{otherChatUserName}</LineTitle>
        {userType !== "ADMIN" && (
          <LineLabel>
            {chat.last
              ? moment.unix(chat.last.time).fromNow()
              : moment.unix(chat.createdTime).fromNow()}
          </LineLabel>
        )}
        {userType === "ADMIN" && (
          <LineLabel>
            {otherChatUserType === "VENUE"
              ? "Venue Manager"
              : "Event Coordinator"}
          </LineLabel>
        )}
      </LineText>
      <LineCaret>
        <CaretIcon size={20} color={colors.gray} style={{ marginTop: 3 }} />
      </LineCaret>
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: OwnProps) => ({
  userType: selectors.userType(state),
  chat: selectors.chat(ownProps.chatId)(state),
  otherChatUserName: selectors.otherChatUserName(ownProps.chatId)(state),
  otherChatUserImage: selectors.otherChatUserImage(ownProps.chatId)(state),
  otherChatUserType: selectors.otherChatUserType(ownProps.chatId)(state),
  otherChatUserInitials: selectors.otherChatUserInitials(ownProps.chatId)(state)
});
const connector = connect(mapState);

export default connector(withRouter(ChatLine));
